import {
	IModel,
	db,
	IIdentifiable,
	enumOrDefault,
	VIPUser,
	IVIPUser,
	IVIPUserOptions,
} from "@/loader";
import dayjs, { Dayjs } from "dayjs";

export enum VipEventType {
	created = "created",
	modified = "modified",
	deleted = "deleted",
	trashed = "trashed",
	generated = "generated",
	uploaded = "uploaded",
	sent = "sent",
	signed = "signed",
	refused = "refused",
	changeRequest = "request_change",
	validationRequest = "request_validation",
	verificationRequest = "request_verification",
	sendRequest = "request_send",
	verified = "verified",
	validated = "validated",
	extended = "extended",
}

export function vipEventTypeOrDefault(value: any, defaultValue: VipEventType = VipEventType.created): VipEventType {
	return <VipEventType>enumOrDefault(value, defaultValue, Object.values(VipEventType));
}

export function getVipEventTypeLabel(value: VipEventType): string {
	switch (value) {
		case VipEventType.created:
			return window.wp.i18n._x("Created", "Event", "vip-vue");
		case VipEventType.modified:
			return window.wp.i18n._x("Modified", "Event", "vip-vue");
		case VipEventType.deleted:
			return window.wp.i18n._x("Deleted", "Event", "vip-vue");
		case VipEventType.trashed:
			return window.wp.i18n._x("Trashed", "Event", "vip-vue");
		case VipEventType.generated:
			return window.wp.i18n._x("Generated", "Event", "vip-vue");
		case VipEventType.uploaded:
			return window.wp.i18n._x("Uploaded", "Event", "vip-vue");
		case VipEventType.sent:
			return window.wp.i18n._x("Sent", "Event", "vip-vue");
		case VipEventType.signed:
			return window.wp.i18n._x("Signed", "Event", "vip-vue");
		case VipEventType.refused:
			return window.wp.i18n._x("Refused", "Event", "vip-vue");
		case VipEventType.changeRequest:
			return window.wp.i18n._x("Change request", "Event", "vip-vue");
		case VipEventType.validationRequest:
			return window.wp.i18n._x("Validation request", "Event", "vip-vue");
		case VipEventType.verificationRequest:
			return window.wp.i18n._x("Verification request", "Event", "vip-vue");
		case VipEventType.sendRequest:
			return window.wp.i18n._x("Send request", "Event", "vip-vue");
		case VipEventType.verified:
			return window.wp.i18n._x("Verified", "Event", "vip-vue");
		case VipEventType.validated:
			return window.wp.i18n._x("Validated", "Event", "vip-vue");
		case VipEventType.extended:
			return window.wp.i18n._x("Extended", "Event", "vip-vue");
		default:
			return window.wp.i18n._x("Unknown Type", "Event", "vip-vue");
	}
}

export function getVipEventTypeList(): { value: any, text: string }[] {
	return [
		{ value: "created", text: window.wp.i18n._x("Created", "Event", "vip-vue") },
		{ value: "modified", text: window.wp.i18n._x("Modified", "Event", "vip-vue") },
		{ value: "deleted", text: window.wp.i18n._x("Deleted", "Event", "vip-vue") },
		{ value: "trashed", text: window.wp.i18n._x("Trashed", "Event", "vip-vue") },
		{ value: "generated", text: window.wp.i18n._x("Generated", "Event", "vip-vue") },
		{ value: "uploaded", text: window.wp.i18n._x("Uploaded", "Event", "vip-vue") },
		{ value: "sent", text: window.wp.i18n._x("Sent", "Event", "vip-vue") },
		{ value: "signed", text: window.wp.i18n._x("Signed", "Event", "vip-vue") },
		{ value: "refused", text: window.wp.i18n._x("Refused", "Event", "vip-vue") },
		{ value: "request_change", text: window.wp.i18n._x("Change request", "Event", "vip-vue") },
		{ value: "request_validation", text: window.wp.i18n._x("Validation request", "Event", "vip-vue") },
		{ value: "request_verification", text: window.wp.i18n._x("Verification request", "Event", "vip-vue") },
		{ value: "request_send", text: window.wp.i18n._x("Send request", "Event", "vip-vue") },
		{ value: "verified", text: window.wp.i18n._x("Verified", "Event", "vip-vue") },
		{ value: "validated", text: window.wp.i18n._x("Validated", "Event", "vip-vue") },
		{ value: "extended", text: window.wp.i18n._x("Extended", "Event", "vip-vue") },
	];
}

export type VIPEventFields = "id" | "fk_author" | "date" | "type" | "description";

/**
 * Event Base Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPEventBase extends IIdentifiable {
	/** Id */
	id?: number;
	/** Author ID */
	fk_author?: number;
	/** Type */
	type?: VipEventType;
	/** Date */
	date?: Dayjs;
	/** Description */
	description?: string | null;

	/** Author */
	author?: IVIPUser | null;
}

/**
 * Event Base Options Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPEventOptions {
	/** Indicates whether the Id should be included or not. Default true. */
	id?: boolean;
	/** Indicates whether the Author ID should be included or not. Default true. */
	fk_author?: boolean;
	/** Indicates whether the Type should be included or not. Default true. */
	type?: boolean;
	/** Indicates whether the Date should be included or not. Default true. */
	date?: boolean;
	/** Indicates whether the Description should be included or not. Default true. */
	description?: boolean;
	/** Indicates whether the Author should be included or not. Default false. */
	author?: boolean | IVIPUserOptions;
}

/**
 * Event Base Class Logic.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export class VIPEventBase implements IVIPEventBase, IModel<IVIPEventBase> {
	/** Id */
	public id!: number;
	/** Author ID */
	public fk_author!: number;
	/** Type */
	public type!: VipEventType;
	/** Date */
	public date!: Dayjs;
	/** Description */
	public description!: string | null;

	/**
	 * Get Author
	 */
	public get author(): VIPUser | null {
		return db.users.get(this.fk_author);
	}

	/**
	 * Set Author
	 */
	public set author(value: VIPUser | null) {
		this.fk_author = Number.orDefault(value?.id);
	}

	/**
	 * Check if this Type is Created.
	 *
	 * @return bool
	 */
	public get isTypeCreated(): boolean {
		return this.type === VipEventType.created;
	}

	/**
	 * Check if this Type is Modified.
	 *
	 * @return bool
	 */
	public get isTypeModified(): boolean {
		return this.type === VipEventType.modified;
	}

	/**
	 * Check if this Type is Deleted.
	 *
	 * @return bool
	 */
	public get isTypeDeleted(): boolean {
		return this.type === VipEventType.deleted;
	}

	/**
	 * Check if this Type is Trashed.
	 *
	 * @return bool
	 */
	public get isTypeTrashed(): boolean {
		return this.type === VipEventType.trashed;
	}

	/**
	 * Check if this Type is Generated.
	 *
	 * @return bool
	 */
	public get isTypeGenerated(): boolean {
		return this.type === VipEventType.generated;
	}

	/**
	 * Check if this Type is Uploaded.
	 *
	 * @return bool
	 */
	public get isTypeUploaded(): boolean {
		return this.type === VipEventType.uploaded;
	}

	/**
	 * Check if this Type is Sent.
	 *
	 * @return bool
	 */
	public get isTypeSent(): boolean {
		return this.type === VipEventType.sent;
	}

	/**
	 * Check if this Type is Signed.
	 *
	 * @return bool
	 */
	public get isTypeSigned(): boolean {
		return this.type === VipEventType.signed;
	}

	/**
	 * Check if this Type is Refused.
	 *
	 * @return bool
	 */
	public get isTypeRefused(): boolean {
		return this.type === VipEventType.refused;
	}

	/**
	 * Check if this Type is Change request.
	 *
	 * @return bool
	 */
	public get isTypeChangeRequest(): boolean {
		return this.type === VipEventType.changeRequest;
	}

	/**
	 * Check if this Type is Validation request.
	 *
	 * @return bool
	 */
	public get isTypeValidationRequest(): boolean {
		return this.type === VipEventType.validationRequest;
	}

	/**
	 * Check if this Type is Verification request.
	 *
	 * @return bool
	 */
	public get isTypeVerificationRequest(): boolean {
		return this.type === VipEventType.verificationRequest;
	}

	/**
	 * Check if this Type is Send request.
	 *
	 * @return bool
	 */
	public get isTypeSendRequest(): boolean {
		return this.type === VipEventType.sendRequest;
	}

	/**
	 * Check if this Type is Verified.
	 *
	 * @return bool
	 */
	public get isTypeVerified(): boolean {
		return this.type === VipEventType.verified;
	}

	/**
	 * Check if this Type is Validated.
	 *
	 * @return bool
	 */
	public get isTypeValidated(): boolean {
		return this.type === VipEventType.validated;
	}

	/**
	 * Check if this Type is Extended.
	 *
	 * @return bool
	 */
	public get isTypeExtended(): boolean {
		return this.type === VipEventType.extended;
	}

	/**
	 * Get the Type label
	 *
	 * @return string
	 */
	public getTypeLabel(): string {
		return getVipEventTypeLabel(this.type);
	}

	/**
	 * Constructor.
	 * @param data
	 */
	constructor (data: IVIPEventBase = {}) {
		this.load(data);
	}

	/**
	 * Set all properties with the data provided or a default value.
	 *
	 * All properties will be set, either with the value provided or with their default value.
	 *
	 * If you want to set only a subset of this instance's properties, use {@link VIPEventBase.update} instead.
	 *
	 * @param data
	 */
	public load(data: IVIPEventBase) {
		this.id = Number.orDefault(data.id);
		this.fk_author = Number.orDefault(data.fk_author);
		this.type = vipEventTypeOrDefault(data.type);
		this.date = dayjs.orDefault(data.date);
		this.description = String.nullable(data.description);
		if (data.author) {
			db.users.insertOrUpdate(data.author);
		}
	}

	/**
	 * Set the properties with the data provided.
	 *
	 * Only the properties defined in data will be updated. Falsy values will be treated, only `undefined` is not.
	 *
	 * If you want to redefine all properties, use {@link VIPEventBase.load} instead.
	 *
	 * @param data
	 */
	public update(data: IVIPEventBase) {
		if (data.id !== undefined) {
			this.id = Number.orDefault(data.id);
		}
		if (data.fk_author !== undefined) {
			this.fk_author = Number.orDefault(data.fk_author);
		}
		if (data.type !== undefined) {
			this.type = vipEventTypeOrDefault(data.type);
		}
		if (data.date !== undefined) {
			this.date = dayjs.orDefault(data.date);
		}
		if (data.description !== undefined) {
			this.description = String.nullable(data.description);
		}
		if (data.author) {
			db.users.insertOrUpdate(data.author);
		}
	}
}
